import type { ApiSessionData } from '../api/types';
import type { DcId, SharedSessionData } from '../types';

import {
  DC_IDS,
  DEBUG,
  SESSION_ACCOUNT_PREFIX,
  SESSION_LEGACY_USER_KEY,
} from '../config';
import { ACCOUNT_SLOT, storeAccountData, writeSlotSession } from './multiaccount';

export function hasStoredSession() {
  if (!ACCOUNT_SLOT) {
    const legacyAuthJson = localStorage.getItem(SESSION_LEGACY_USER_KEY);
    if (legacyAuthJson) {
      try {
        const userAuth = JSON.parse(legacyAuthJson);
        return Boolean(userAuth && userAuth.dcID);
      } catch (err) {
        return false;
      }
    }
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);
  return Boolean(slotData && slotData.dcId);
}

export function storeSession(sessionData: ApiSessionData) {
  const {
    mainDcId, keys, salts, saltValidUntils, isTest,
  } = sessionData;

  const currentSlotData = loadSlotSession(ACCOUNT_SLOT);
  const newSlotData: SharedSessionData = {
    ...currentSlotData,
    dcId: mainDcId,
    isTest,
  };

  DC_IDS.forEach((dcId) => {
    delete newSlotData[`dc${dcId as DcId}_auth_key`];
    delete newSlotData[`dc${dcId as DcId}_server_salt`];
    delete newSlotData[`dc${dcId as DcId}_server_salt_valid_until`];
  });

  Object.keys(keys).map(Number).forEach((dcId) => {
    newSlotData[`dc${dcId as DcId}_auth_key`] = keys[dcId];
  });
  Object.keys(salts || {}).map(Number).forEach((dcId) => {
    newSlotData[`dc${dcId as DcId}_server_salt`] = salts![dcId];
  });
  Object.keys(saltValidUntils || {}).map(Number).forEach((dcId) => {
    newSlotData[`dc${dcId as DcId}_server_salt_valid_until`] = saltValidUntils![dcId];
  });

  if (!ACCOUNT_SLOT) {
    storeLegacySession(sessionData, currentSlotData?.userId);
  }

  writeSlotSession(ACCOUNT_SLOT, newSlotData);
}

function storeLegacySession(sessionData: ApiSessionData, currentUserId?: string) {
  const {
    mainDcId, keys, salts, saltValidUntils, isTest,
  } = sessionData;

  localStorage.setItem(SESSION_LEGACY_USER_KEY, JSON.stringify({
    dcID: mainDcId,
    id: currentUserId,
    test: isTest,
  }));
  localStorage.setItem('dc', String(mainDcId));
  DC_IDS.forEach((dcId) => {
    localStorage.removeItem(`dc${dcId}_auth_key`);
    localStorage.removeItem(`dc${dcId}_server_salt`);
    localStorage.removeItem(`dc${dcId}_server_salt_valid_until`);
  });
  Object.keys(keys).map(Number).forEach((dcId) => {
    localStorage.setItem(`dc${dcId}_auth_key`, JSON.stringify(keys[dcId]));
  });
  Object.keys(salts || {}).map(Number).forEach((dcId) => {
    localStorage.setItem(`dc${dcId}_server_salt`, JSON.stringify(salts![dcId]));
  });
  Object.keys(saltValidUntils || {}).map(Number).forEach((dcId) => {
    localStorage.setItem(`dc${dcId}_server_salt_valid_until`, String(saltValidUntils![dcId]));
  });
}

export function clearStoredSession(slot?: number) {
  if (!slot) {
    clearStoredLegacySession();
  }

  localStorage.removeItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`);
}

function clearStoredLegacySession() {
  [
    SESSION_LEGACY_USER_KEY,
    'dc',
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt_valid_until`),
  ].forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function loadStoredSession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const slotData = loadSlotSession(ACCOUNT_SLOT);

  if (!slotData) {
    if (ACCOUNT_SLOT) return undefined;
    return loadStoredLegacySession();
  }

  const sessionData: ApiSessionData = {
    mainDcId: slotData.dcId,
    keys: DC_IDS.reduce((acc, dcId) => {
      const key = slotData[`dc${dcId}_auth_key` as const];
      if (key) {
        acc[dcId] = key;
      }
      return acc;
    }, {} as Record<number, string>),
    salts: DC_IDS.reduce((acc, dcId) => {
      const salt = slotData[`dc${dcId}_server_salt` as const];
      if (salt) {
        acc[dcId] = salt;
      }
      return acc;
    }, {} as Record<number, string>),
    saltValidUntils: DC_IDS.reduce((acc, dcId) => {
      const validUntil = slotData[`dc${dcId}_server_salt_valid_until` as const];
      if (typeof validUntil === 'number') {
        acc[dcId] = validUntil;
      }
      return acc;
    }, {} as Record<number, number>),
    isTest: slotData.isTest || undefined,
  };

  if (sessionData.salts && !Object.keys(sessionData.salts).length) {
    delete sessionData.salts;
  }
  if (sessionData.saltValidUntils && !Object.keys(sessionData.saltValidUntils).length) {
    delete sessionData.saltValidUntils;
  }

  return sessionData;
}

function loadStoredLegacySession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const userAuth = JSON.parse(localStorage.getItem(SESSION_LEGACY_USER_KEY) || 'null');
  if (!userAuth) {
    return undefined;
  }
  const mainDcId = Number(userAuth.dcID);
  const isTest = userAuth.test;
  const keys: Record<number, string> = {};
  const salts: Record<number, string> = {};
  const saltValidUntils: Record<number, number> = {};

  DC_IDS.forEach((dcId) => {
    try {
      const key = localStorage.getItem(`dc${dcId}_auth_key`);
      if (key) {
        keys[dcId] = JSON.parse(key);
      }
      const salt = localStorage.getItem(`dc${dcId}_server_salt`);
      if (salt) {
        salts[dcId] = JSON.parse(salt);
      }
      const validUntilStr = localStorage.getItem(`dc${dcId}_server_salt_valid_until`);
      if (validUntilStr) {
        const parsed = Number(validUntilStr);
        if (Number.isFinite(parsed)) {
          saltValidUntils[dcId] = parsed;
        }
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load stored session', err);
      }
    }
  });

  if (!Object.keys(keys).length) return undefined;

  return {
    mainDcId,
    keys,
    salts: Object.keys(salts).length ? salts : undefined,
    saltValidUntils: Object.keys(saltValidUntils).length ? saltValidUntils : undefined,
    isTest,
  };
}

export function loadSlotSession(slot: number | undefined): SharedSessionData | undefined {
  try {
    const data = JSON.parse(localStorage.getItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`) || '{}') as SharedSessionData;
    if (!data.dcId) return undefined;
    return data;
  } catch (e) {
    return undefined;
  }
}

export function updateSessionUserId(currentUserId: string) {
  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (!slotData) return;
  storeAccountData(ACCOUNT_SLOT, { userId: currentUserId });
}
